import { useState } from 'react';

import { TWO } from '@/constants';
import { useCurrentUser } from '@/hooks';
import {
  isAdmin,
  isCoach,
  isMomentsSession,
  isSupport,
  type Session,
  SessionListType,
  SessionWarningAction,
} from '@/types';
import { ConfirmationSnackbar, Divider } from '@/ui';
import { isInNumberOfDays } from '@/utils';

import type { FC } from 'react';

import CancelSession from '@/components/Cal/cancelSession';
import RescheduleSession from '@/components/Cal/rescheduleSession';
import SessionWarning from '@/components/Session/SessionWarning';
import { SessionCard } from '@/components/Sessions/Card';
import { useSessionActions } from '@/features/coachSchedule/useSessionActions';
import { sessionActionRoute } from '@/utils/sessionActionRoute';

interface CancellationControlsProps {
  hideCalendarOptions?: boolean;
  session: Session;
}

const CancellationControls: FC<CancellationControlsProps> = ({
  hideCalendarOptions = false,
  session,
}) => {
  const { user } = useCurrentUser();
  const [openCancel, setOpenCancel] = useState<Session | null>();
  const [openReschedule, setOpenReschedule] = useState<Session | null>();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [warningAction, setWarningAction] =
    useState<SessionWarningAction | null>(null);
  const canUseMemberNativeMomentsCancel =
    user.id === session.userId &&
    !isCoach(user) &&
    !isAdmin(user) &&
    !isSupport(user);
  const canUseCoachNativeMomentsReschedule =
    user.id === session.coachId &&
    isCoach(user) &&
    !isAdmin(user) &&
    !isSupport(user);
  const coachActions = useSessionActions(user.profile?.timezone, {
    disabled: !canUseCoachNativeMomentsReschedule,
  });
  // Native-capable members on a Moments session use the redesigned flows,
  // which own their own 48h speedbumps. Skipping the legacy SessionWarning
  // here avoids a double speedbump. Cal.com sessions keep SessionWarning.
  const isNativeMomentsSession =
    canUseMemberNativeMomentsCancel && isMomentsSession(session);

  const onCloseCancelSession = () => setOpenCancel(null);
  const onCloseReschedule = () => setOpenReschedule(null);
  const onCancel = () => {
    if (!isNativeMomentsSession && isInNumberOfDays(session.startedAt, TWO)) {
      setWarningAction(SessionWarningAction.CANCEL);
      return;
    }

    setOpenCancel(session);
  };
  const onReschedule = (targetSession: Session = session) => {
    if (
      canUseCoachNativeMomentsReschedule &&
      sessionActionRoute({
        action: 'reschedule',
        session: targetSession,
        viewer: 'associated-coach',
      }) === 'mento-coach'
    ) {
      coachActions.openReschedule(targetSession);
      return;
    }

    setOpenReschedule(targetSession);
  };
  const onCancelled = () => setShowCancelConfirmation(true);
  const onCloseCancelConfirmation = () => setShowCancelConfirmation(false);

  return (
    <>
      <ConfirmationSnackbar
        message="Session cancelled."
        open={showCancelConfirmation}
        onClose={onCloseCancelConfirmation}
      />
      <SessionCard
        session={session}
        type={SessionListType.Upcoming}
        isEmbedded
        onCancel={!hideCalendarOptions ? onCancel : undefined}
        onClickPath={`././sessions/${session.id}`}
        onReschedule={!hideCalendarOptions ? onReschedule : undefined}
      />
      {!!openCancel && (
        <>
          <Divider />
          <CancelSession
            allowMemberMomentsCancellation={canUseMemberNativeMomentsCancel}
            close={onCloseCancelSession}
            session={openCancel}
            onCancelled={onCancelled}
            onRescheduleInstead={() => {
              // Hand off to reschedule for the same session without a mutation.
              const next = openCancel;
              setOpenCancel(null);
              onReschedule(next);
            }}
          />
        </>
      )}
      {!!openReschedule && (
        <RescheduleSession
          close={onCloseReschedule}
          enableNativeMoments={canUseMemberNativeMomentsCancel}
          session={openReschedule}
        />
      )}
      {coachActions.modals}
      {!!warningAction && (
        <SessionWarning
          action={warningAction}
          close={() => setWarningAction(null)}
          session={session}
          onCancel={setOpenCancel}
          onReschedule={onReschedule}
        />
      )}
    </>
  );
};

export default CancellationControls;
